import { LocationMap } from '../../components/LocationMap';
import { ScreenStatus } from '../../components/ScreenStatus';
import { BigDisplaysStatus } from '../../components/BigDisplaysStatus';
import './StatusDataPage.scss';
import { SensorsStatus } from '../../components/SensorsStatus';
import { useEffect, useState } from 'react';
import { getSensors } from '../../services/sensorData';
import { getLocationMappings, getLocations } from '../../services/locationData';
import { useDisplays } from '../../contexts/DisplaysContext';
import { Sensor } from '../../types/Sensor';
import { LocationMapping, Location } from '../../types/Locations';
import { getDevices } from '../../services/deviceData';
import { Device } from '../../types/Device';
import { greenCircleIcon, yellowCircleIcon, greyCircleIcon } from '../../utils/images';
import { BigDisplay } from '../../types/BigDisplay';

export const StatusDataPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'screens' | 'displays'>('screens');
  const [displays, setDisplays] = useState<Device[]>([]);
  const [mappingList, setMappingList] = useState<LocationMapping[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [screens, setScreens] = useState<Device[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const { displays: bigDisplays, loading: displaysLoading, error: displaysError, refreshDisplays } = useDisplays();
  const [bigDisplaysWithLocation, setBigDisplaysWithLocation] = useState<BigDisplay[]>([]);
  const storeId = localStorage.getItem('storeId');

  const fetchSensors = async () => {
    if (storeId) {
      try {
        const response = await getSensors(storeId);
        if (response) {
          setSensors(response);
        } else {
          console.error('Failed to fetch sensors or response data is missing');
        }
      } catch (error) {
        console.error('Error fetching sensors:', error);
      }
    }
  };

  const fetchDisplays = async () => {
    try {
      const deviceResponse = await getDevices(storeId as string);
      setDisplays(deviceResponse.items || deviceResponse);
    } catch (error) {
      console.error('Error fetching displays:', error);
      alert("Failed to fetch displays. Please check console for details.");
    }
  };

  const fetchMappedLocations = async () => {
    if (storeId) {
      try {
        const mappingsResponse = await getLocationMappings(storeId);
        if (mappingsResponse) {
          setMappingList(mappingsResponse);
        } else {
          console.error('Failed to fetch location mappings or response data is missing');
        }
      } catch (error) {
        console.error('Error fetching location mappings:', error);
      }
    }
  };

  const fetchLocations = async () => {
    if (storeId) {
      try {
        const locationsResponse = await getLocations(storeId);
        setLocations(locationsResponse);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    }
  };

  const refreshData = async () => {
    await Promise.all([
      fetchSensors(),
      // UNCOMMENT IF USING TABS, THIS WAS ADDED BECAUSE OF ESL ISSUE
      // fetchDisplays(),
      fetchMappedLocations(),
      fetchLocations(),
      refreshDisplays()
    ]);
  };

  useEffect(() => {
    fetchSensors();
    // UNCOMMENT IF USING TABS, THIS WAS ADDED BECAUSE OF ESL ISSUE
    // fetchDisplays();
    fetchMappedLocations();
    fetchLocations();
  }, [storeId]);

  useEffect(() => {
    if (bigDisplays && mappingList) {
      const locationMap = new Map<number, string>();
      mappingList.forEach(mapping => {
        if (mapping.display_id !== null) {
          locationMap.set(mapping.display_id, mapping.location_code);
        }
      });

      const bigScreensWithLocation: BigDisplay[] = bigDisplays.map(display => {
        // Try to parse the ID as a number
        const displayIdNumber = display.id;

        if (!isNaN(+displayIdNumber) && locationMap.has(displayIdNumber)) {
          const location_code = locationMap.get(displayIdNumber);
          return { ...display, location_code: location_code || undefined };
        } else {
          return { ...display, location_code: undefined };
        }
      });

      setBigDisplaysWithLocation(bigScreensWithLocation);
    }
  }, [bigDisplays, mappingList]);

  useEffect(() => {
    if (displays && mappingList) {
      const locationMap = new Map<string | null, string>();
      mappingList.forEach(mapping => {
        if (mapping.label_id !== null) {
          locationMap.set(mapping.label_id, mapping.location_code);
        }
      });

      const screensWithLocation: Device[] = displays.map(display => {
        // Try to parse the ID as a number
        const displayIdNumber = display.id;

        if ((displayIdNumber) && locationMap.has(displayIdNumber)) {
          const location_code = locationMap.get(displayIdNumber);
          return { ...display, location_code: location_code || undefined };
        } else {
          return { ...display, location_code: undefined };
        }
      });

      setScreens(screensWithLocation);
    }
  }, [displays, mappingList]);

  return (
    <div className="statusDataPage">
      {storeId && (
        <LocationMap
          locations={locations}
          screens={screens}
          displays={bigDisplaysWithLocation}
          sensors={sensors}
          storeId={storeId}
          onLocationAdded={refreshData}
        />
      )}

      <div className='statusDataPage__container'>
        {/* UNCOMMENT IF USING TABS, THIS WAS ADDED BECAUSE OF ESL ISSUE */}
        {/* <div className="statusDataPage__header">
          <div className="statusDataPage__tabs-container">
            <div className="statusDataPage__tabs">
              <button 
                className={`statusDataPage__tab ${activeTab === 'screens' ? 'statusDataPage__tab--active' : ''}`}
                onClick={() => setActiveTab('screens')}
              >
                <p className="statusDataPage__tab-title">Labels Status</p>
              </button>
              <button 
                className={`statusDataPage__tab ${activeTab === 'displays' ? 'statusDataPage__tab--active' : ''}`}
                onClick={() => setActiveTab('displays')}
              >
                <p className="statusDataPage__tab-title">Displays Status</p>
              </button>
            </div>
          </div>

          <div className="statusDataPage__legend">
            <div className="statusDataPage__indicators">
              <img src={greenCircleIcon} alt="Icon" />
              <span className="statusDataPage__indicator active">Active</span>
              <img src={yellowCircleIcon} alt="Icon" />
              <span className="statusDataPage__indicator fault">Fault</span>
              <img src={greyCircleIcon} alt="Icon" />
              <span className="statusDataPage__indicator inactive">Inactive</span>
            </div>
          </div>
        </div> */}

        {/* UNCOMMENT IF USING TABS, THIS WAS ADDED BECAUSE OF ESL ISSUE */}
        {/* {activeTab === 'screens' ? (
          <ScreenStatus 
            screens={screens} 
            locations={locations} 
            onRefresh={refreshData} 
          />
        ) : (
          <BigDisplaysStatus 
            displays={bigDisplaysWithLocation} 
            locations={locations} 
            onRefresh={refreshData} 
          />
        )} */}

        {/* REMOVE THIS COMPONENT IF WANT TO USE TABS WITH LABELS, THIS WAS ADDED BECAUSE OF ESL ISSUE
          MUST ALSO EDIT INSIDE COMPONENT ITSELF
        */}
        <BigDisplaysStatus
          displays={bigDisplaysWithLocation}
          locations={locations}
          onRefresh={refreshData}
        />

        <SensorsStatus
          sensors={sensors}
          locations={locations}
          onRefresh={refreshData}
        />
      </div>
    </div>
  );
};
